import {
  AccountBookOutlined,
  BarChartOutlined,
  BuildOutlined,
  CalendarOutlined,
  FileDoneOutlined,
  FileOutlined,
  FileProtectOutlined,
  PaperClipOutlined,
  ProfileOutlined,
  QuestionCircleOutlined,
  ShoppingCartOutlined,
  SolutionOutlined,
  SwapOutlined
} from "@ant-design/icons"
import type { ComponentType, CSSProperties } from "react"

type IconComponent = ComponentType<{
  className?: string
  style?: CSSProperties
}>

// Single source of truth for a Document Type's icon — reused by the table
// cell, its dropdown, bulk document type, filters and File Details, so a
// type never gets a different icon depending on where it's rendered.
//
// Unlike Status, `DOCUMENT_TYPE_SEED` (apps/templates_management/
// list_options.py) marks every value `is_system=False` — a company can add
// its own types beyond these defaults — so this map covers the seeded set
// and anything else falls back to a plain document icon rather than
// breaking or guessing.
const DOCUMENT_TYPE_ICONS: Record<string, IconComponent> = {
  contract: FileProtectOutlined,
  proposal: FileDoneOutlined,
  invoice: AccountBookOutlined,
  change_request: SwapOutlined,
  drawing: BuildOutlined,
  rfi: QuestionCircleOutlined,
  submittal: ProfileOutlined,
  report: BarChartOutlined,
  purchase_order: ShoppingCartOutlined,
  schedule: CalendarOutlined,
  meeting_minutes: SolutionOutlined,
  other: PaperClipOutlined
}

// The same fixed set Settings' icon picker offers (an admin-configured
// type's own `icon`, keyed by @ant-design/icons' own names) — kept as a
// separate map from the one above rather than merged into it, since that one
// is keyed by *slug* and this one by *icon name*; a seeded type's slug and
// icon name happen to differ (e.g. "contract" → "file-protect").
export const ICON_NAME_TO_COMPONENT: Record<string, IconComponent> = {
  "file-protect": FileProtectOutlined,
  "file-done": FileDoneOutlined,
  "account-book": AccountBookOutlined,
  swap: SwapOutlined,
  build: BuildOutlined,
  "question-circle": QuestionCircleOutlined,
  profile: ProfileOutlined,
  "bar-chart": BarChartOutlined,
  "shopping-cart": ShoppingCartOutlined,
  calendar: CalendarOutlined,
  solution: SolutionOutlined,
  "paper-clip": PaperClipOutlined
}

/** Builds the value → icon-name lookup every Document Type surface needs to
 *  pass into `renderDocumentTypeDecoration`'s override — one place for the
 *  `.reduce()` instead of five near-identical copies. */
export const documentTypeIconLookup = (
  options: { value: string; icon?: string }[]
): Record<string, string> =>
  options.reduce<Record<string, string>>((lookup, option) => {
    if (option.icon) lookup[option.value] = option.icon
    return lookup
  }, {})

export const documentTypeIcon = (
  documentType: string | null | undefined,
  /** The option's own configured icon name, when the caller has it — takes
   *  priority over the static slug map so an admin's choice always wins. */
  iconOverride?: string
): IconComponent =>
  (iconOverride && ICON_NAME_TO_COMPONENT[iconOverride]) ||
  (documentType && DOCUMENT_TYPE_ICONS[documentType]) ||
  FileOutlined
